import { FIELD_POSITIONS_BATCH_MAX } from '@groupe-reca/contracts'
import { ServerCommandError } from '@/infrastructure/supabase/client'
import type { LocalStore } from '@/platform/localStore'
import type { MissionRepository } from '../MissionRepository'

export interface SyncResult {
  sent: number
  rejected: number
  pending: number
  /** Dernière erreur : `NETWORK` (on réessaiera) ou code de refus du serveur (compte révoqué, mission introuvable). */
  error: string | null
}

/**
 * Envoi des positions et interruptions en attente, par lots, mission par mission. Un point n'est supprimé de la
 * tablette qu'après accusé de réception du serveur (accepté ou déjà reçu) ou refus définitif motivé. En cas de
 * refus d'accès (compte désactivé, session révoquée), rien n'est envoyé ni effacé.
 */
export class PositionSync {
  private running: Promise<SyncResult> | null = null

  constructor(
    private readonly store: LocalStore,
    private readonly repository: MissionRepository,
  ) {}

  flush(): Promise<SyncResult> {
    this.running ??= this.run().finally(() => {
      this.running = null
    })
    return this.running
  }

  private async run(): Promise<SyncResult> {
    let sent = 0
    let rejected = 0
    let error: SyncResult['error'] = null
    try {
      for (const gap of await this.store.pendingGaps()) {
        await this.repository.sendGap(gap)
        // Une interruption encore ouverte reste en file : sa fin sera renvoyée (même identifiant).
        if (gap.endedAt !== null) await this.store.removeGap(gap.id)
      }
      for (;;) {
        const batch = await this.store.pendingPositions(FIELD_POSITIONS_BATCH_MAX)
        if (batch.length === 0) break
        const missionId = batch[0]?.missionId ?? ''
        const points = batch.filter((point) => point.missionId === missionId)
        const ack = await this.repository.sendPositions(
          missionId,
          points.map((point) => ({
            id: point.id,
            capturedAt: point.capturedAt,
            latitude: point.latitude,
            longitude: point.longitude,
            accuracy: point.accuracy,
            heading: point.heading ?? null,
            speed: point.speed ?? null,
          })),
        )
        const done = [...ack.acknowledged, ...ack.rejected.map((item) => item.id)]
        if (done.length === 0) break
        await this.store.removePositions(done)
        sent += ack.acknowledged.length
        rejected += ack.rejected.length
        if (points.length < FIELD_POSITIONS_BATCH_MAX && batch.length === points.length) break
      }
    } catch (failure) {
      // Réseau ou réponse illisible : NETWORK; refus métier : son code. Rien n'est effacé dans les deux cas.
      error = failure instanceof ServerCommandError ? failure.code : 'NETWORK'
    }
    return { sent, rejected, pending: await this.store.countPositions(), error }
  }
}
